import { Injectable, inject, signal, effect, computed } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { lastValueFrom } from 'rxjs';
import { AuthService } from './auth.service';
import { Capacitor } from '@capacitor/core';
import { AppLifecycleService } from './app-lifecycle.service';

// ---------- Types ----------
type BillingFrequency = 'monthly' | 'yearly';

// ---------- New plan matrix (includes free tier) ----------
export interface PlanFeature {
  name: string;
  credits?: number;
  monitorLimit?: number;
  priceMonthly?: number;
  priceYearly?: number;
  variantIdMonthly?: string;
  variantIdYearly?: string;
  variantIdOneTime?: string;
  isOneTime?: boolean;
}

/**
 * Plan definitions used for checkout URLs and static feature display.
 * Replace placeholder credits / monitor limits for paid plans when the
 * business values are finalised.
 */
export const PLAN_MATRIX: Record<string, PlanFeature> = {
  free: {
    name: 'Free',
    credits: 7,
    monitorLimit: 1,
    priceMonthly: 0,
    priceYearly: 0,
  },
  scout: {
    name: 'Scout',
    variantIdMonthly: '1733c7d7-99b4-481f-b80c-227c2067f00f',
    variantIdYearly: '569c4a7b-26a7-4d9e-9aec-0b1847dcc9ab',
  },
  operator: {
    name: 'Operator',
    variantIdMonthly: '3f09329e-558b-45f9-90be-1613a47e40fa',
    variantIdYearly: '338b93a1-d333-46e5-8e0f-be9c2852c243',
  },
  apex: {
    name: 'Apex',
    variantIdMonthly: 'd16063ae-34e9-4d49-a3c2-2acb8c47eb16',
    variantIdYearly: 'f54f53bd-5674-4495-ba87-6d655e278bad',
  },
  boost: {
    name: 'Boost',
    variantIdOneTime: 'c13a4984-cb78-46f0-98c7-e6447d3e882a',
    isOneTime: true,
  },
};

const STORE_SLUG = 'rtila-ai';

// ---------- TypeScript interfaces ----------
export interface BillingStatus {
  credits: number;
  subscription?: {
    planName: string;
    status: string; // 'active', 'past_due', 'cancelled', etc.
    renewsAt?: string;
    endsAt?: string;
  };
  monitorLimit?: number | string;
  /** Current number of active monitors (supplied by the backend). */
  monitorCount?: number;
}

export interface LedgerEntry {
  id: string;
  amount: number;
  reason: string;
  referenceType: string;
  createdAt: string;
}

@Injectable({ providedIn: 'root' })
export class BillingService {
  private http = inject(HttpClient);
  private authService = inject(AuthService);
  private appLifecycle = inject(AppLifecycleService);

  private _status = signal<BillingStatus | null>(null);
  private _ledger = signal<LedgerEntry[]>([]);

  private isCheckoutPending = false;

  /** True when the user is on the permanent free plan. */
  readonly isFreePlan = computed(
    () => this._status()?.subscription?.planName === 'free'
  );

  constructor() {
    effect(() => {
      const active = this.appLifecycle.isActive();
      if (active && this.isCheckoutPending) {
        this.isCheckoutPending = false;
        this.loadStatus();
      }
    });
  }

  get status() { return this._status.asReadonly(); }
  get ledger() { return this._ledger.asReadonly(); }

  /** Fetch the current billing status (credits, plan, monitor limit) */
  async loadStatus(): Promise<void> {
    try {
      const resp: any = await lastValueFrom(this.http.get('/billing/status'));
      if (resp.success) {
        const sub = resp.subscription;
        const monitorCount = resp.monitorCount !== undefined ? Number(resp.monitorCount) : undefined;
        this._status.set({
          credits: resp.credits ?? 0,
          subscription: sub
            ? {
                planName: sub.planName ?? '',
                status: sub.status ?? 'active',
                renewsAt: sub.renewsAt ?? undefined,
                endsAt: sub.endsAt ?? undefined,
              }
            : undefined,
          monitorLimit: resp.monitorLimit ?? undefined,
          monitorCount,
        });
      }
    } catch (err) {
      console.error('Failed to load billing status', err);
    }
  }

  /** Fetch the transaction ledger (history) */
  async loadLedger(limit = 20, offset = 0): Promise<void> {
    try {
      const resp: any = await lastValueFrom(
        this.http.get(`/billing/ledger?limit=${limit}&offset=${offset}`)
      );
      if (resp.success) {
        // Ensure the signal always holds an array (the API may return null or omit the field)
        const entries = Array.isArray(resp.entries) ? resp.entries : [];
        this._ledger.set(entries);
      }
    } catch (err) {
      console.error('Failed to load ledger', err);
    }
  }

  /**
   * Fetch a page of ledger entries without touching the legacy signal.
   * Designed to be used with the infinite‑scroll PaginationState helper.
   */
  async fetchLedgerPage(limit: number, offset: number): Promise<LedgerEntry[]> {
    try {
      const resp: any = await lastValueFrom(
        this.http.get(`/billing/ledger?limit=${limit}&offset=${offset}`)
      );
      if (resp.success && Array.isArray(resp.entries)) {
        return resp.entries;
      }
      return [];
    } catch (err) {
      console.error('Failed to fetch ledger page', err);
      return [];
    }
  }

  /**
   * Build a Lemon Squeezy checkout URL for the given plan and billing frequency.
   * Returns `null` when the plan is the free tier – no checkout URL is generated.
   * @param plan      One of 'scout', 'operator', 'apex', or 'boost'.
   * @param frequency `'monthly'` or `'yearly'` (ignored for one‑time plans); defaults to `'monthly'`.
   */
  getCheckoutUrl(plan: string, frequency: BillingFrequency = 'monthly'): string | null {
    // Prevent checkout for the free tier — users must select a paid plan.
    if (plan === 'free') {
      return null;
    }

    const planDef = PLAN_MATRIX[plan];
    if (!planDef) {
      throw new Error(`Unknown billing plan "${plan}".`);
    }

    let variantId: string | undefined;

    if (planDef.isOneTime) {
      variantId = planDef.variantIdOneTime;
    } else {
      variantId =
        frequency === 'yearly'
          ? planDef.variantIdYearly ?? planDef.variantIdMonthly
          : planDef.variantIdMonthly;
    }

    if (!variantId) {
      throw new Error(
        `No variant ID configured for plan "${plan}" with frequency "${frequency}".`
      );
    }

    const userId = this.authService.userId();
    return `https://${STORE_SLUG}.lemonsqueezy.com/checkout/buy/${variantId}?checkout[custom][user_id]=${encodeURIComponent(userId)}`;
  }

  /** Open the Lemon Squeezy checkout overlay (web) or system browser (native). */
  openCheckout(plan: string, frequency: BillingFrequency = 'monthly'): void {
    const url = this.getCheckoutUrl(plan, frequency);
    if (!url) {
      // No checkout URL for the free tier — user must upgrade to a paid plan.
      return;
    }
    if (Capacitor.isNativePlatform()) {
      this.isCheckoutPending = true;
      window.open(url, '_system');
    } else {
      try {
        if ((window as any).LemonSqueezy?.Checkout) {
          (window as any).LemonSqueezy.Checkout.open({ url });
        } else {
          window.open(url, '_blank');
        }
      } catch {
        window.open(url, '_blank');
      }
    }
  }

  /** Returns the hosted customer portal URL using the user's email. */
  getCustomerPortalUrl(): string {
    const email = this.authService.email();
    return `https://app.lemonsqueezy.com/my-orders?email=${encodeURIComponent(email)}`;
  }
}
